import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'

function RadioCardGroup({ name, label, options, required = false }) {
  return (
    <div>
      <p className="block text-gray-700 font-semibold mb-3">{label}{required ? ' *' : ''}</p>
      <div className="grid gap-3">
        {options.map((option) => {
          const id = `${name}-${option.value.replace(/\s+/g, '-').toLowerCase()}`
          return (
            <label
              key={option.value}
              htmlFor={id}
              className="flex items-start gap-3 border rounded-xl p-4 cursor-pointer hover:border-blue-400 transition"
            >
              <input
                id={id}
                type="radio"
                name={name}
                value={option.value}
                required={required}
                className="mt-1 h-4 w-4 shrink-0"
              />
              <span className="text-gray-800 leading-6">{option.label}</span>
            </label>
          )
        })}
      </div>
    </div>
  )
}

export default function ContactForm() {
  const navigate = useNavigate()
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)

    try {
      const formData = new FormData(e.target)

      if (!formData.get('_subject')) {
        formData.append('_subject', 'New website enquiry — 2merative')
      }

      const response = await fetch('https://formspree.io/f/mgvlqzjv', {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' },
      })

      if (response.ok) {
        navigate('/thankyou')
      } else {
        alert('Something went wrong. Please try again.')
        setSubmitting(false)
      }
    } catch (error) {
      console.error('Form submission error:', error)
      alert('An error occurred. Please try again later.')
      setSubmitting(false)
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-3xl mx-auto bg-white shadow-md rounded px-6 sm:px-8 pt-6 pb-8 mb-10 mt-10 space-y-6 text-left"
    >
      <h2 className="text-2xl font-semibold text-blue-800 text-center">
        Let’s talk about your website
      </h2>

      <p className="text-center text-gray-600 max-w-2xl mx-auto">
        Fill this out and we will get a clear picture of where your business is at, what you need, and what the right next step looks like.
      </p>

      <div className="hidden" aria-hidden="true">
        <label>
          Do not fill this out
          <input name="_gotcha" tabIndex="-1" autoComplete="off" />
        </label>
      </div>

      <div>
        <label className="block text-gray-700 font-semibold mb-1">Name *</label>
        <input
          name="name"
          required
          className="w-full border rounded-lg p-3 text-base"
        />
      </div>

      <div>
        <label className="block text-gray-700 font-semibold mb-1">Email *</label>
        <input
          type="email"
          name="email"
          required
          className="w-full border rounded-lg p-3 text-base"
        />
      </div>

      <div>
        <label className="block text-gray-700 font-semibold mb-1">Phone *</label>
        <input
          name="phone"
          required
          className="w-full border rounded-lg p-3 text-base"
        />
      </div>

      <div>
        <label className="block text-gray-700 font-semibold mb-1">Business name *</label>
        <input
          name="businessName"
          required
          className="w-full border rounded-lg p-3 text-base"
        />
      </div>

      <RadioCardGroup
        name="projectType"
        label="What do you need help with?"
        required
        options={[
          { value: 'I need a new website', label: 'I need a new website' },
          {
            value: 'I already have a website and need it improved',
            label: 'I already have a website and need it improved',
          },
          { value: 'I am not sure yet', label: 'I am not sure yet' },
        ]}
      />

      <div>
        <label className="block text-gray-700 font-semibold mb-1">
          If you already have a website, what is the website address?
        </label>
        <input
          name="currentWebsite"
          placeholder="https://"
          className="w-full border rounded-lg p-3 text-base"
        />
      </div>

      <RadioCardGroup
        name="websiteGoal"
        label="What is the main goal of the website?"
        required
        options={[
          { value: 'Get more enquiries', label: 'Get more enquiries' },
          { value: 'Look more professional', label: 'Look more professional' },
          {
            value: 'Make it easier for customers to contact us',
            label: 'Make it easier for customers to contact us',
          },
          {
            value: 'Replace an old or poor website',
            label: 'Replace an old or poor website',
          },
          {
            value: 'Support bookings or appointments',
            label: 'Support bookings or appointments',
          },
          { value: 'Sell products online', label: 'Sell products online' },
          { value: 'Other', label: 'Other' },
        ]}
      />

      <div>
        <label className="block text-gray-700 font-semibold mb-1">
          What type of business are you?
        </label>
        <input
          name="businessType"
          placeholder="For example: chiropractor, tradie, café, club, consultant"
          className="w-full border rounded-lg p-3 text-base"
        />
      </div>

      <RadioCardGroup
        name="haveLogo"
        label="Do you already have branding or a logo?"
        options={[
          { value: 'Yes', label: 'Yes' },
          { value: 'No', label: 'No' },
          { value: 'Some, but it needs work', label: 'Some, but it needs work' },
        ]}
      />

      <div>
        <label className="block text-gray-700 font-semibold mb-3">
          What features do you need?
        </label>
        <div className="grid sm:grid-cols-2 gap-3 text-sm text-gray-700">
          {[
            'Contact form',
            'Online booking',
            'Online shop',
            'Blog or articles',
            'Portfolio or gallery',
            'Events or fixtures',
          ].map((feature) => {
            const id = `feature-${feature.replace(/\s+/g, '-').toLowerCase()}`
            return (
              <label
                key={feature}
                htmlFor={id}
                className="flex items-start gap-3 border rounded-xl p-4 cursor-pointer hover:border-blue-400 transition"
              >
                <input
                  id={id}
                  type="checkbox"
                  name="features"
                  value={feature}
                  className="mt-1 h-4 w-4 shrink-0"
                />
                <span className="text-gray-800 leading-6">{feature}</span>
              </label>
            )
          })}
        </div>
      </div>

      <RadioCardGroup
        name="hostingHelp"
        label="Do you need help with domain or hosting?"
        options={[
          { value: 'Yes, I need help with both', label: 'Yes, I need help with both' },
          { value: 'I need help with hosting only', label: 'I need help with hosting only' },
          { value: 'I need help with domain only', label: 'I need help with domain only' },
          { value: 'No, I already have them sorted', label: 'No, I already have them sorted' },
          { value: 'Not sure', label: 'Not sure' },
        ]}
      />

      <RadioCardGroup
        name="timeline"
        label="What timeline are you working to?"
        options={[
          { value: 'As soon as possible', label: 'As soon as possible' },
          { value: 'Within 1 month', label: 'Within 1 month' },
          { value: 'Within 2 to 3 months', label: 'Within 2 to 3 months' },
          { value: 'No fixed timeline yet', label: 'No fixed timeline yet' },
        ]}
      />

      <RadioCardGroup
        name="budget"
        label="What budget range are you considering?"
        options={[
          { value: 'Under $2,000', label: 'Under $2,000' },
          { value: '$2,000 to $4,000', label: '$2,000 to $4,000' },
          { value: '$4,000 to $7,000', label: '$4,000 to $7,000' },
          { value: '$7,000+', label: '$7,000+' },
          { value: 'Not sure yet', label: 'Not sure yet' },
        ]}
      />

      <div>
        <label className="block text-gray-700 font-semibold mb-1">
          Tell us a bit about what you need
        </label>
        <textarea
          name="extraNotes"
          rows="5"
          placeholder="Share anything useful here. What is not working, what you want the website to do better, or what kind of business situation you are in."
          className="w-full border rounded-lg p-3 text-base"
        />
      </div>

      <div className="text-center">
        <button
          type="submit"
          disabled={submitting}
          className="bg-blue-700 hover:bg-blue-900 text-white font-bold py-3 px-6 rounded-lg transition duration-300"
        >
          {submitting ? 'Sending...' : 'Send Enquiry'}
        </button>
      </div>
    </form>
  )
}